import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import { useTheme } from '@/contexts/ThemeContext'
import { usePublicClient, useChainId, useChains } from 'wagmi'
import { getEnv } from '@/config/env'
import { useNotification } from '@/hooks/useNotification'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import type { Abi } from 'viem'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import {
  fetchExecutedTransactionsFromService,
  type ExecutedTransaction,
} from '@/utils/safeTransactionService'
import {
  DEFAULT_TOKEN_DECIMALS,
  TRANSACTIONS_PER_PAGE,
  TRANSACTIONS_RELOAD_DELAY_COUNT,
  TRANSACTION_HISTORY_RELOAD_DELAY,
  API_REQUEST_DELAY_MS,
} from '@/constants/main'
import {
  getTransactionType,
  formatDateForFilter,
  extractAddressFromData,
} from './Actions/utils'
import type {
  TransactionHistoryItem,
  TransactionFilters,
} from './Actions/types'
import TransactionHistory from './Actions/TransactionHistory'
import ExportModal from './Actions/ExportModal'
import { copyToClipboard } from '@/utils/clipboard'

export default function TransactionHistoryBlock() {
  const { showSuccess, showError } = useNotification()
  const { colors } = useTheme()
  const muiTheme = useMuiTheme()
  const isMobileOrTablet = useMediaQuery(muiTheme.breakpoints.down('lg'))
  const { stngProxy, treasurySafe, operationalSafeFactory } = useMemo(
    () => getEnv(),
    []
  )
  const publicClient = usePublicClient()
  const chainId = useChainId()
  const chains = useChains()
  const chain = useMemo(
    () => chains.find((item) => item.id === chainId),
    [chains, chainId]
  )

  const { isReady: isSafeReady } = useSafe(treasurySafe as `0x${string}`)

  const StyledTextField = useMemo(() => {
    const placeholderColor =
      (colors as { yellow?: string; primary: string }).yellow || colors.primary
    const placeholderStyles = {
      color: `${placeholderColor} !important`,
      opacity: '1 !important',
    }

    return styled(TextField)(() => ({
      '& .MuiOutlinedInput-root': {
        color: colors.text,
        '& fieldset': {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: `${colors.primary} !important`,
        },
        '&:hover fieldset': {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: `${colors.primary} !important`,
        },
        '&.Mui-focused fieldset': {
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: `${(colors as { yellow: string }).yellow} !important`,
        },
      },
      '& .MuiInputLabel-root': {
        color: colors.text,
        opacity: 0.7,
        '&.Mui-focused': {
          color: placeholderColor,
        },
      },
      '& .MuiInputBase-input': {
        cursor: 'pointer',
        '&::placeholder': placeholderStyles,
        '&::-webkit-input-placeholder': placeholderStyles,
        '&::-moz-placeholder': placeholderStyles,
        '&:-ms-input-placeholder': placeholderStyles,
      },
      '& .MuiInputAdornment-root': {
        cursor: 'pointer',
        '& .MuiIconButton-root': {
          color: `${colors.primary} !important`,
          '&:hover': {
            backgroundColor: `${colors.primary}20`,
          },
        },
        '& .MuiSvgIcon-root': {
          color: `${colors.primary} !important`,
        },
      },
    }))
  }, [colors])

  const [transactionHistory, setTransactionHistory] = useState<
    TransactionHistoryItem[]
  >([])
  const [isLoadingTransactionHistory, setIsLoadingTransactionHistory] =
    useState(false)
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1)
  const [totalHistoryCount, setTotalHistoryCount] = useState<number>(0)
  const [showFilters, setShowFilters] = useState(false)
  const [filterDateFrom, setFilterDateFrom] = useState<string>('')
  const [filterDateTo, setFilterDateTo] = useState<string>('')
  const [filterNonce, setFilterNonce] = useState<string>('')
  const [filterType, setFilterType] = useState<string>('')
  const [appliedFilters, setAppliedFilters] = useState<TransactionFilters>({})
  const [allTransactions, setAllTransactions] = useState<
    TransactionHistoryItem[]
  >([])
  const [decimals, setDecimals] = useState<number>(DEFAULT_TOKEN_DECIMALS)
  const [showExportModal, setShowExportModal] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [csvContentForCopy, setCsvContentForCopy] = useState<string | null>(
    null
  )
  const csvTextFieldRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (csvContentForCopy && csvTextFieldRef.current) {
      const timer = setTimeout(() => {
        if (csvTextFieldRef.current) {
          csvTextFieldRef.current.select()
          csvTextFieldRef.current.setSelectionRange(0, csvContentForCopy.length)
        }
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [csvContentForCopy])

  useEffect(() => {
    const fetchDecimals = async () => {
      if (!publicClient || !stngProxy || !chain || !publicClient.readContract) {
        return
      }
      try {
        const contractDecimals = (await publicClient.readContract({
          address: stngProxy as `0x${string}`,
          abi: STNGImplementationAbi as Abi,
          functionName: 'decimals',
        })) as number
        setDecimals(contractDecimals)
      } catch (error) {
        console.error('Failed to fetch decimals:', error)
      }
    }
    fetchDecimals()
  }, [publicClient, stngProxy, chain])

  const handleApplyFilters = useCallback(() => {
    const filters: TransactionFilters = {}
    if (filterDateFrom) {
      filters.executionDateFrom = formatDateForFilter(filterDateFrom, false)
    }

    if (filterDateTo) {
      filters.executionDateTo = formatDateForFilter(filterDateTo, true)
    }

    if (filterNonce) {
      filters.nonce = filterNonce
    }

    if (filterType) {
      filters.type = filterType
    }

    setAppliedFilters(filters)
    setCurrentHistoryPage(1)
  }, [filterDateFrom, filterDateTo, filterNonce, filterType])

  const loadTransactionHistory = useCallback(
    async (filters?: TransactionFilters) => {
      if (!isSafeReady || !treasurySafe || !chain) {
        setIsLoadingTransactionHistory(false)
        return
      }

      try {
        setIsLoadingTransactionHistory(true)

        const apiFilters: TransactionFilters = {}
        if (filters !== undefined) {
          apiFilters.executionDateFrom = filters.executionDateFrom
          apiFilters.executionDateTo = filters.executionDateTo
          apiFilters.nonce = filters.nonce
        } else {
          apiFilters.executionDateFrom = appliedFilters.executionDateFrom
          apiFilters.executionDateTo = appliedFilters.executionDateTo
          apiFilters.nonce = appliedFilters.nonce
        }

        const allApiTransactions: ExecutedTransaction[] = []
        let offset = 0
        const limit = 1000
        let hasMore = true

        while (hasMore) {
          const treasuryResult = await fetchExecutedTransactionsFromService(
            treasurySafe,
            chain.id,
            limit,
            offset,
            apiFilters
          )

          if (
            !treasuryResult.success ||
            !treasuryResult.transactions ||
            treasuryResult.transactions.length === 0
          ) {
            hasMore = false
            break
          }

          allApiTransactions.push(...treasuryResult.transactions)

          if (treasuryResult.transactions.length < limit) {
            hasMore = false
          } else {
            offset += limit
            if (hasMore) {
              await new Promise((resolve) =>
                setTimeout(resolve, API_REQUEST_DELAY_MS)
              )
            }
          }
        }

        if (allApiTransactions.length > 0) {
          const typePromises = allApiTransactions.map((txData) => {
            let amount: string | undefined
            let toAddress: string | undefined

            if (txData.dataDecoded && typeof txData.dataDecoded === 'object') {
              const dataDecoded = txData.dataDecoded as {
                method?: string
                parameters?: Array<{
                  name: string
                  type: string
                  value: string
                }>
              }

              if (dataDecoded.parameters) {
                const guyParam = dataDecoded.parameters.find(
                  (p) => p.name === 'guy' || p.name === 'to'
                )
                const wadParam = dataDecoded.parameters.find(
                  (p) =>
                    p.name === 'wad' ||
                    p.name === 'amount' ||
                    p.name === 'value'
                )

                if (guyParam) {
                  toAddress = guyParam.value
                }
                if (wadParam) {
                  amount = wadParam.value
                }
              }
            }

            return getTransactionType(
              txData.tx,
              treasurySafe,
              stngProxy,
              operationalSafeFactory,
              publicClient
            ).then((type) => {
              let finalToAddress = toAddress
              if (type === 'Delete' || type === 'Block' || type === 'Unblock') {
                const extractedAddress = extractAddressFromData(
                  txData.tx.data.data
                )
                if (extractedAddress) {
                  finalToAddress = extractedAddress
                }
              }

              return {
                tx: txData.tx,
                hash: txData.safeTxHash,
                type,
                safeAddress: treasurySafe,
                executionDate: txData.executionDate,
                transactionHash: txData.transactionHash,
                isSuccessful: txData.isSuccessful,
                executor: txData.executor,
                amount,
                to: finalToAddress,
              }
            })
          })
          const typedTxs = await Promise.all(typePromises)

          setAllTransactions(typedTxs)
        } else {
          setAllTransactions([])
        }
      } catch (error) {
        console.error('Failed to load transaction history:', error)
        setAllTransactions([])
      } finally {
        setIsLoadingTransactionHistory(false)
      }
    },
    [
      isSafeReady,
      treasurySafe,
      chain,
      appliedFilters,
      stngProxy,
      operationalSafeFactory,
      publicClient,
    ]
  )

  useEffect(() => {
    if (!isSafeReady || !treasurySafe || !chain) {
      return
    }
    loadTransactionHistory()
  }, [
    isSafeReady,
    treasurySafe,
    chain,
    appliedFilters.executionDateFrom,
    appliedFilters.executionDateTo,
    appliedFilters.nonce,
    loadTransactionHistory,
  ])

  useEffect(() => {
    const handleTransactionExecuted = () => {
      loadTransactionHistory()

      for (let i = 1; i <= TRANSACTIONS_RELOAD_DELAY_COUNT; i++) {
        setTimeout(() => {
          loadTransactionHistory()
        }, TRANSACTION_HISTORY_RELOAD_DELAY * i)
      }
    }

    window.addEventListener('transaction-executed', handleTransactionExecuted)

    return () => {
      window.removeEventListener(
        'transaction-executed',
        handleTransactionExecuted
      )
    }
  }, [loadTransactionHistory])

  const totalHistoryPages = Math.ceil(totalHistoryCount / TRANSACTIONS_PER_PAGE)

  useEffect(() => {
    if (allTransactions.length === 0) {
      return
    }

    const typeFilter = appliedFilters.type || filterType
    const filteredTxs = typeFilter
      ? allTransactions.filter((tx) => tx.type === typeFilter)
      : allTransactions

    const offset = (currentHistoryPage - 1) * TRANSACTIONS_PER_PAGE
    const paginatedTxs = filteredTxs.slice(
      offset,
      offset + TRANSACTIONS_PER_PAGE
    )

    setTransactionHistory(paginatedTxs)
    setTotalHistoryCount(filteredTxs.length)
  }, [allTransactions, appliedFilters.type, filterType, currentHistoryPage])

  useEffect(() => {
    if (filterType && filterType !== appliedFilters.type) {
      setCurrentHistoryPage(1)
    }
  }, [filterType, appliedFilters.type])

  useEffect(() => {
    if (
      transactionHistory.length === 0 &&
      currentHistoryPage > 1 &&
      totalHistoryCount > 0
    ) {
      setCurrentHistoryPage(currentHistoryPage - 1)
    }
  }, [transactionHistory.length, currentHistoryPage, totalHistoryCount])

  const handleCopyAddress = useCallback(
    async (address: string) => {
      try {
        await copyToClipboard(address)
        showSuccess('Address copied!')
      } catch (error) {
        console.error('Failed to copy address:', error)
        showError('Failed to copy address')
      }
    },
    [showSuccess, showError]
  )

  const fetchAllTransactionsForExport = useCallback(
    async (
      dateFrom: string,
      dateTo: string
    ): Promise<TransactionHistoryItem[]> => {
      if (!isSafeReady || !treasurySafe || !chain) {
        return []
      }

      const filters: TransactionFilters = {
        executionDateFrom: formatDateForFilter(dateFrom, false),
        executionDateTo: formatDateForFilter(dateTo, true),
      }

      const allTransactions: TransactionHistoryItem[] = []
      let offset = 0
      const limit = 1000
      let hasMore = true

      while (hasMore) {
        const result = await fetchExecutedTransactionsFromService(
          treasurySafe,
          chain.id,
          limit,
          offset,
          filters
        )

        if (
          !result.success ||
          !result.transactions ||
          result.transactions.length === 0
        ) {
          hasMore = false
          break
        }

        const typePromises = result.transactions.map((txData) => {
          let amount: string | undefined
          let toAddress: string | undefined

          if (txData.dataDecoded && typeof txData.dataDecoded === 'object') {
            const dataDecoded = txData.dataDecoded as {
              method?: string
              parameters?: Array<{
                name: string
                type: string
                value: string
              }>
            }

            if (dataDecoded.parameters) {
              const guyParam = dataDecoded.parameters.find(
                (p) => p.name === 'guy' || p.name === 'to'
              )
              const wadParam = dataDecoded.parameters.find(
                (p) =>
                  p.name === 'wad' || p.name === 'amount' || p.name === 'value'
              )

              if (guyParam) {
                toAddress = guyParam.value
              }
              if (wadParam) {
                amount = wadParam.value
              }
            }
          }

          return getTransactionType(
            txData.tx,
            treasurySafe,
            stngProxy,
            operationalSafeFactory,
            publicClient
          )
            .then((type) => {
              let finalToAddress = toAddress
              if (type === 'Delete' || type === 'Block' || type === 'Unblock') {
                const extractedAddress = extractAddressFromData(
                  txData.tx.data.data
                )
                if (extractedAddress) {
                  finalToAddress = extractedAddress
                }
              }

              return {
                tx: txData.tx,
                hash: txData.safeTxHash,
                type: type || 'Unknown',
                safeAddress: treasurySafe,
                executionDate: txData.executionDate,
                transactionHash: txData.transactionHash,
                isSuccessful: txData.isSuccessful,
                executor: txData.executor,
                amount,
                to: finalToAddress,
              }
            })
            .catch((error) => {
              console.error('Error getting transaction type:', error)
              return {
                tx: txData.tx,
                hash: txData.safeTxHash,
                type: 'Unknown',
                safeAddress: treasurySafe,
                executionDate: txData.executionDate,
                transactionHash: txData.transactionHash,
                isSuccessful: txData.isSuccessful,
                executor: txData.executor,
                amount,
                to: toAddress,
              }
            })
        })

        const typedTxs = await Promise.all(typePromises)
        allTransactions.push(...typedTxs)

        if (result.transactions.length < limit) {
          hasMore = false
        } else {
          offset += limit
          if (hasMore) {
            await new Promise((resolve) =>
              setTimeout(resolve, API_REQUEST_DELAY_MS)
            )
          }
        }
      }

      return allTransactions
    },
    [
      isSafeReady,
      treasurySafe,
      chain,
      stngProxy,
      operationalSafeFactory,
      publicClient,
    ]
  )

  const convertToCSV = useCallback(
    (transactions: TransactionHistoryItem[]): string => {
      const headers = [
        'Date',
        'Type',
        'Hash',
        'Transaction Hash',
        'Status',
        'Executor',
        'To',
        'Amount',
        'Nonce',
      ]

      const formatDateForCSV = (dateString: string | null): string => {
        if (!dateString) return ''
        try {
          return new Date(dateString).toISOString()
        } catch {
          return dateString
        }
      }

      const rows = transactions.map((tx) => {
        const date = formatDateForCSV(tx.executionDate)
        const type = tx.type || 'Unknown'
        const hash = tx.hash || ''
        const txHash = tx.transactionHash || ''
        const status =
          tx.isSuccessful === true
            ? 'Success'
            : tx.isSuccessful === false
            ? 'Failed'
            : 'Unknown'
        const executor = tx.executor || ''
        const to = tx.to || ''
        const amount = tx.amount
          ? (BigInt(tx.amount) / BigInt(10 ** decimals)).toString()
          : ''
        const nonce = tx.tx.data.nonce?.toString() || ''

        return [date, type, hash, txHash, status, executor, to, amount, nonce]
      })

      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ),
      ].join('\n')

      return csvContent
    },
    [decimals]
  )

  const handleExport = useCallback(
    async (dateFrom: string, dateTo: string, type?: string) => {
      if (!isSafeReady || !treasurySafe || !chain) {
        showError('Cannot export: Safe not ready')
        return
      }

      setIsExporting(true)

      try {
        const transactions = await fetchAllTransactionsForExport(
          dateFrom,
          dateTo
        )

        const filteredTransactions = type
          ? transactions.filter((tx) => tx.type === type)
          : transactions

        if (filteredTransactions.length === 0) {
          showError('No transactions found for the selected period and type')
          setIsExporting(false)
          return
        }

        const csvContent = convertToCSV(filteredTransactions)
        // Add UTF-8 BOM for proper encoding on mobile devices
        const BOM = '\uFEFF'
        const csvWithBOM = BOM + csvContent

        const fileName = `transactions_${dateFrom}_${dateTo}.csv`

        const isMetaMaskWebView =
          navigator.userAgent.includes('MetaMaskMobile') ||
          navigator.userAgent.includes('Mobi') ||
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

        if (isMetaMaskWebView) {
          setCsvContentForCopy(csvContent)
          setShowExportModal(false)
          showSuccess('CSV content ready. Please copy it from the dialog.')
        } else {
          const blob = new Blob([csvWithBOM], {
            type: 'text/csv',
          })

          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')

          link.href = url
          link.download = fileName
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()

          setTimeout(() => {
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
          }, 100)
        }

        showSuccess(`Exported ${filteredTransactions.length} transactions`)
        setShowExportModal(false)
      } catch (error) {
        console.error('Failed to export transactions:', error)
        showError('Failed to export transactions')
      } finally {
        setIsExporting(false)
      }
    },
    [
      isSafeReady,
      treasurySafe,
      chain,
      fetchAllTransactionsForExport,
      convertToCSV,
      showSuccess,
      showError,
    ]
  )

  return (
    <>
      <TransactionHistory
        transactions={transactionHistory}
        isLoading={isLoadingTransactionHistory}
        currentPage={currentHistoryPage}
        totalPages={totalHistoryPages}
        showFilters={showFilters}
        filterDateFrom={filterDateFrom}
        filterDateTo={filterDateTo}
        filterNonce={filterNonce}
        filterType={filterType}
        chain={chain}
        isMobileOrTablet={isMobileOrTablet}
        decimals={decimals}
        StyledTextField={
          StyledTextField as React.ElementType<
            React.ComponentProps<typeof TextField>
          >
        }
        onRefresh={() => {
          setAppliedFilters({})
          setFilterDateFrom('')
          setFilterDateTo('')
          setFilterNonce('')
          setFilterType('')
          setCurrentHistoryPage(1)
          loadTransactionHistory({})
        }}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onPageChange={setCurrentHistoryPage}
        onDateFromChange={(date) => setFilterDateFrom(date)}
        onDateToChange={(date) => setFilterDateTo(date)}
        onNonceChange={(nonce) => setFilterNonce(nonce)}
        onTypeChange={(type) => setFilterType(type)}
        onClearFilters={() => {
          setFilterDateFrom('')
          setFilterDateTo('')
          setFilterNonce('')
          setFilterType('')
        }}
        onApplyFilters={handleApplyFilters}
        onCopyAddress={handleCopyAddress}
        onExportClick={() => setShowExportModal(true)}
      />
      <Dialog
        open={!!csvContentForCopy}
        onClose={() => setCsvContentForCopy(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.background,
            border: `2px solid ${colors.primary}`,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.primary }}>
          Copy CSV Content
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: colors.text, opacity: 0.8 }}
          >
            MetaMask WebView doesn't support file downloads. Please copy the CSV
            content below and save it as a .csv file.
          </Typography>
          <TextField
            multiline
            fullWidth
            rows={10}
            value={csvContentForCopy || ''}
            inputRef={csvTextFieldRef}
            onFocus={(e) => {
              const target = e.target as HTMLTextAreaElement
              target.select()
              target.setSelectionRange(0, csvContentForCopy?.length || 0)
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.text,
                backgroundColor: colors.background,
                '& fieldset': {
                  borderColor: colors.primary,
                },
                '&:hover fieldset': {
                  borderColor: colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary,
                },
              },
            }}
            inputProps={{
              style: {
                fontFamily: 'monospace',
                fontSize: '12px',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCsvContentForCopy(null)}
            sx={{
              color: colors.primary,
              borderColor: colors.primary,
              border: '1px solid',
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!csvContentForCopy) {
                showError('No CSV content to copy')
                return
              }

              if (csvTextFieldRef.current) {
                const textarea = csvTextFieldRef.current
                textarea.focus()
                textarea.select()
                textarea.setSelectionRange(0, csvContentForCopy.length)

                try {
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(csvContentForCopy)
                    showSuccess('CSV content copied to clipboard!')
                    return
                  }
                } catch (clipboardError) {
                  console.warn('Clipboard API failed:', clipboardError)
                }

                try {
                  interface DocumentWithExecCommand extends Document {
                    execCommand(
                      commandId: string,
                      showUI?: boolean,
                      value?: string
                    ): boolean
                  }
                  const successful = (
                    document as DocumentWithExecCommand
                  ).execCommand('copy')
                  if (successful) {
                    showSuccess('CSV content copied to clipboard!')
                  } else {
                    throw new Error('execCommand failed')
                  }
                } catch (execError) {
                  console.error('Copy failed:', execError)
                  showError(
                    'Auto-copy failed. Text is selected - please copy manually (Ctrl+C / Cmd+C or long-press and select Copy)'
                  )
                }
              } else {
                try {
                  await copyToClipboard(csvContentForCopy)
                  showSuccess('CSV content copied to clipboard!')
                } catch (error) {
                  console.error('Copy failed:', error)
                  showError(
                    'Failed to copy. Please select the text above and copy manually.'
                  )
                }
              }
            }}
            sx={{
              backgroundColor: colors.primary,
              color: colors.background,
            }}
          >
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>
      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        StyledTextField={
          StyledTextField as React.ElementType<
            React.ComponentProps<typeof TextField>
          >
        }
        isExporting={isExporting}
      />
    </>
  )
}
